import { Component, OnInit, Input, OnDestroy, AfterViewInit, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ChatService } from '../chat.service';
import { SwirlService } from '../swirl.service';
import { ActivatedRoute } from '@angular/router';
import {RateLimitService} from "../../rate-limit.service";

@Component({
  selector: 'app-chat-box',
  templateUrl: './chat-box.component.html',
  styleUrls: ['./chat-box.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ChatBoxComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() autoFocus: boolean = false;
  @ViewChild('inputElement') inputElement!: ElementRef;
  @Input() isConversation: boolean = false;

  searchForm: UntypedFormGroup;
  searchButtonLabel: string = 'SEND';
  aiAssistantPlaceholder: string = 'Ask me anything, I\'m here to help...';

  messages: any[] = [];
  chatId: number | undefined;
  isLoading: boolean = false;
  isComposing: boolean = false; // "true" means user is in the middle of typing japanese text using IME

  private messagesSubscription: Subscription | undefined;
  private isConversationSubscription: Subscription | undefined;
  private isLoadingSubscription: Subscription | undefined;

  constructor(
      private fb: UntypedFormBuilder,
      private chatService: ChatService,
      private swirlService: SwirlService,
      private route: ActivatedRoute,
      protected rl: RateLimitService
  ) {
    this.searchForm = this.fb.group({
      query: [{ value: '', disabled: this.isLoading }]
    });
  }

  get isSearchButtonDisabled(): boolean {
    return this.searchForm.get('query')?.value?.trim() === '';
  }

  get isThrottled(): boolean {
    return this.rl.isThrottled;
  }

  ngOnInit() {
    // Extract chatId from the query parameters
    this.route.queryParams.subscribe(params => {
      const chatIdParam = params['chat_id'];
      if (chatIdParam) {
        this.chatId = +chatIdParam; // Convert to number
        this.chatService.setChatId(this.chatId);
        this.subscribeToMessages(this.chatId);
        this.fetchMessages(this.chatId);
      } else {
        console.info('No chat_id found in the URL. Starting a new chat session.');
      }
    });

    // Subscribe to conversation status changes
    this.isConversationSubscription = this.chatService.isConversationStarted$.subscribe(isStarted => {
      if (isStarted && this.chatId !== undefined) {
        this.fetchMessages(this.chatId);
      }
    });

    // Subscribe to isLoading state from ChatService
    this.isLoadingSubscription = this.chatService.isLoading$.subscribe(isLoading => {
      this.setLoadingState(isLoading);
    });

    // Get the BrandingConfiguration values
    this.swirlService.getBrandingValues('config').subscribe((values) => {
      this.aiAssistantPlaceholder = values.ai_assistant_placeholder ?? this.aiAssistantPlaceholder;
    });
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) {
      this.focusInput();
    }
  }

  private subscribeToMessages(chatId: number) {
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
    }
    this.messagesSubscription = this.chatService.getMessagesObservable(chatId).subscribe({
      next: (updatedMessages) => {
        this.messages = updatedMessages;
        if (this.autoFocus) {
          this.focusInput();
        }
      },
      error: (error) => {
        console.error('Error subscribing to messages:', error);
      },
    });
  }

  fetchMessages(chatId: number) {
    if (this.rl.isThrottled) return;
    this.swirlService.getMessages(chatId).subscribe({
      next: (resp) => {
        this.chatService.setChatId(chatId);
        this.chatService.setMessages(chatId, resp);
        this.messages = this.chatService.getMessages(chatId);

        if (this.autoFocus) {
          this.focusInput();
        }
      },
      error: (error) => {
        if (error?.status === 429) {
          this.rl.startFromHeaders(error.headers, 10);
          return;
        }
        console.error('Error fetching messages:', error);
      },
    });
  }

  setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;
    const queryControl = this.searchForm.get('query');
    if (isLoading) {
      queryControl?.disable();
    } else {
      queryControl?.enable();
    }
    if (this.autoFocus) {
      this.focusInput();
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      if (this.isComposing || (event as any).isComposing || event.keyCode === 229) { //last 2 are for Safari
        // Don't do anything if user only pressed Enter to input Japanese text
        return;
      }
      if (event.ctrlKey || event.shiftKey) {
        event.preventDefault();
        const queryControl = this.searchForm.get('query');
        const currentValue = queryControl?.value || '';
        queryControl?.setValue(currentValue + '\n');
      } else {
        event.preventDefault();
        this.onSubmit();
      }
    }
  }

  handleCompositionStart(): void {
    this.isComposing = true;
  }
  
  handleCompositionEnd(): void {
    this.isComposing = false;
  }

  onSubmit() {
    if (this.searchForm.valid) {
      const query = this.searchForm.get('query')?.value.trim();
      if (!query) return;

      const userMessage = { role: 'user', content: query };
      let currentChatId = this.chatService.getChatId();

      if (currentChatId === undefined) {
        // (1) Initialize new chat session
        this.swirlService.createChat().subscribe({
          next: (response) => {
            this.chatId = response.id || undefined;
            if (this.chatId === undefined) {
              console.error('Invalid chat ID received from createChat.');
              return;
            }
            this.chatService.setChatId(this.chatId);
            this.chatService.changeStatus(true);

            // (2) Add user message
            this.chatService.addMessage(this.chatId, userMessage);

            // (3) Subscribe to messages
            this.subscribeToMessages(this.chatId);

            // (4) Set loading state & send
            this.chatService.setLoading(true);
            this.sendMessage(query, this.chatId);
          },
          error: (error) => {
            console.error('Error initializing chat session:', error);
          },
        });
      } else {
        // We already have a chat
        this.chatId = currentChatId;
        this.chatService.addMessage(this.chatId, userMessage);
        this.chatService.changeStatus(true);

        // Reorder in ChatHistory
        const now = new Date().toISOString();
        this.chatService.notifyChatUpdated(this.chatId, now);

        if (!this.messagesSubscription || this.messagesSubscription.closed) {
          this.subscribeToMessages(this.chatId);
        }

        this.chatService.setLoading(true);
        this.sendMessage(query, this.chatId);
      }

      this.searchForm.reset();
      if (this.autoFocus) {
        this.focusInput();
      }
    }
  }

  sendMessage(query: string, chatId: number) {
    this.swirlService.sendMessage(query, chatId).subscribe({
      next: (response) => {
        // Add assistant message to local store
        this.chatService.addMessage(chatId, {
          id: response.id,
          role: 'assistant',
          time: response.time,
          content: response.content,
          additional_content: response.additional_content,
          rating: null as 0|1|null,
          rated: false,
          show_feedback_box: false,
          feedback_text: '',
          show_thanks_message: false,
          show_other_modal: false,
        });

        // Re-fetch entire conversation
        this.fetchMessages(chatId);

        // Re-emit chat ID for chat-history
        this.chatService.setChatId(chatId);

        // Reorder chat in chat-history
        const updatedAt = new Date().toISOString();
        this.chatService.notifyChatUpdated(chatId, updatedAt);

        // Done loading
        this.chatService.setLoading(false);
      },
      error: (error) => {
        console.error('Error sending message:', error);
        this.chatService.setLoading(false);
      }
    });
  }

  private focusInput(): void {
    if (this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  ngOnDestroy() {
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
    }
    if (this.isConversationSubscription) {
      this.isConversationSubscription.unsubscribe();
    }
    if (this.isLoadingSubscription) {
      this.isLoadingSubscription.unsubscribe();
    }
  }
}
